import { DropdownMenuItem, Icon, SearchBolder, TableColumn, TableRow, Tooltip } from '@aon/aon-tws-common-ui';
import { ActionMenuWrapper } from '@aon/aon-tws-common-ui/build/lib/components/Table';
import { DateUtil } from '@aon/aon-tws-common-ui/build/lib/utils/DateUtil';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import testFlag from '../../../assets/images/icon-testFlag.png';
import ContentConstants from '../../../contentConstants';
import { AudienceSourceType, AudienceStatus, DistributionType, IAudience, IColumn, SeverityType } from '../../../models';
import { AudienceColumns, ActionCreators as AudienceListActionCreators } from '../../../store/AudienceListStore';
import { ActionCreators as DistributionFileActionCreators } from '../../../store/DistributionFileStore';
import { ColumnHelper } from '../../../utilities/ColumnHelper';
import { NavigationHelper } from '../../../utilities/NavigationHelper';
import { useShouldDisableTestEmail } from '../../../utilities/hooks/index';
import { TableLinkWrapper } from '../../common';
import { CustomBanner } from '../../common/CustomBanner';
import { WarningBox } from '../../common/warnings';
import { AudienceMode } from '../AudienceMode';

export type AudienceRowProps = {
	audience: IAudience;
	searchText?: string;
	columns: IColumn[];
	audienceDeleteInProgress: boolean | undefined;
	audienceSourceType: AudienceSourceType | undefined;
	mode: AudienceMode;
};

const BannerDiv = styled.div`
	display: inline-block;
`;

export const AudienceRow = (props: AudienceRowProps) => {
	const dispatch = useDispatch();
	const isTestEmailDisabled = useShouldDisableTestEmail(props.audience.distributionType ?? DistributionType.SingleLinkWithoutDistribution);
	const isValid = props.audience.distributionType !== null && props.audience.distributionType !== undefined;
	const allowDistributionListDownload = isValid && props.audienceSourceType === AudienceSourceType.Hris;

	const visibleColumns = ColumnHelper.GetSelectedColumns(props.columns);

	return <TableRow>{visibleColumns.map((column: IColumn) => getTableColumn(column))}</TableRow>;

	function getTableColumn(column: IColumn) {
		const { audience } = props;
		const key = `${column.name.toLowerCase()}-${audience.audienceId}`;

		switch (column.name) {
			case AudienceColumns.audienceName:
				return (
					<TableColumn key={key}>
						<TableLinkWrapper onClick={!props.audienceDeleteInProgress ? navigate : undefined}>
							{audience.isTestAudience && (
								<BannerDiv>
									<CustomBanner src={testFlag} left={-40} top={-17} />
								</BannerDiv>
							)}
							<SearchBolder text={audience.audienceName} searchText={props.searchText} />
						</TableLinkWrapper>
						{showWarningMessage() && <WarningBox severity={SeverityType.Warning} messages={[ContentConstants.AUDIENCE_NOT_PUBLISHED]} />}
					</TableColumn>
				);
			case AudienceColumns.distributionType:
				return <TableColumn key={key}>{getDistribution()}</TableColumn>;
			case AudienceColumns.employeesCount:
				return (
					<TableColumn key={key}>
						{getEmployeeCount()}
						{getTooltip()}
					</TableColumn>
				);
			case AudienceColumns.reminderEmailsCount:
				return <TableColumn key={key}>{audience.reminderEmailsCount}</TableColumn>;
			case AudienceColumns.openOn:
				return (
					<TableColumn key={key}>
						{!audience.openImmediately || (audience.openImmediately && audience.openOn)
							? DateUtil.formatDateToLocal(new Date(audience.openOn!))
							: 'Immediately'}
					</TableColumn>
				);
			case AudienceColumns.closeOn:
				return <TableColumn key={key}>{audience.closeOn ? DateUtil.formatDateToLocal(new Date(audience.closeOn)) : ''}</TableColumn>;
			case AudienceColumns.actions:
				return getActionsColumn(key);
			case AudienceColumns.copy:
				return (
					<TableColumn key={key}>
						<Icon
							name='copy'
							style={{ cursor: 'pointer' }}
							onClick={() => dispatch(AudienceListActionCreators.ToggleCopyAudienceModal(true, audience))}
						/>
					</TableColumn>
				);
			default:
				return <TableColumn key={key} />;
		}
	}

	function getActionsColumn(key: string) {
		const { audience } = props;

		return (
			<TableColumn key={key}>
				<ActionMenuWrapper>
					{props.audienceSourceType !== AudienceSourceType.Hierarchy && (
						<DropdownMenuItem onClick={() => dispatch(AudienceListActionCreators.ToggleCopyAudienceModal(true, audience))}>
							{ContentConstants.ACTIONMENU_COMMAND_COPY}
						</DropdownMenuItem>
					)}
					{allowDistributionListDownload && (
						<DropdownMenuItem
							onClick={() => dispatch(DistributionFileActionCreators.DownloadDistributionList(audience.surveyId, audience.audienceId))}
						>
							Download name/email list
						</DropdownMenuItem>
					)}
					<DropdownMenuItem disabled={props.audienceDeleteInProgress} onClick={!props.audienceDeleteInProgress ? navigate : undefined}>
						Edit
					</DropdownMenuItem>
					{!isTestEmailDisabled && (
						<DropdownMenuItem onClick={() => dispatch(AudienceListActionCreators.ToggleSendEmailModal(true, audience))}>
							{ContentConstants.BUTTONS_SEND_TEST_EMAIL}
						</DropdownMenuItem>
					)}
					<DropdownMenuItem
						disabled={props.audienceDeleteInProgress}
						onClick={
							!props.audienceDeleteInProgress ? () => dispatch(AudienceListActionCreators.ToggleWarningModal(true, audience)) : undefined
						}
					>
						Delete
					</DropdownMenuItem>
				</ActionMenuWrapper>
			</TableColumn>
		);
	}

	function getDistribution() {
		switch (props.audience.distributionType) {
			case DistributionType.SingleLinkWithoutDistribution:
				return ContentConstants.DISTRIBUTIONTYPE_SINGLELINK_WITHOUTDISTRIBUTION;
			case DistributionType.SingleLink:
				return ContentConstants.DISTRIBUTIONTYPE_SINGLELINK;
			case DistributionType.SingleLinkWithAccessCode:
				return ContentConstants.DISTRIBUTIONTYPE_SINGLELINK_WITHACCESSCODE;
			case DistributionType.Unique:
				return ContentConstants.DISTRIBUTIONTYPE_UNIQUE;
			case DistributionType.GroupBasedLinks:
				return ContentConstants.DISTRIBUTIONTYPE_GROUPBASEDLINKS;
			case DistributionType.SSO:
				return ContentConstants.DISTRIBUTIONTYPE_SSO;
		}
		return '';
	}

	function navigate() {
		const { audience, mode } = props;
		if (
			isValid &&
			((props.mode === AudienceMode.Admin && isPublished(audience.audienceStatus)) || audience.audienceStatus === AudienceStatus.Error)
		) {
			dispatch(NavigationHelper.GoToAudienceMenu(audience.surveyId, audience.audienceId));
		} else {
			dispatch(NavigationHelper.GoToAudienceDetails(audience.surveyId, mode, audience.audienceId));
		}
	}

	function isPublished(audienceStatus: AudienceStatus) {
		switch (audienceStatus) {
			case AudienceStatus.Archived:
			case AudienceStatus.Closed:
			case AudienceStatus.Live:
			case AudienceStatus.Publishing:
			case AudienceStatus.Queued:
			case AudienceStatus.Ready:
			case AudienceStatus.Locked:
			case AudienceStatus.Cancelled:
				return true;
			case AudienceStatus.Error:
			case AudienceStatus.Incomplete:
				return false;
		}
	}

	function showWarningMessage() {
		return props.mode === AudienceMode.Admin && (!isValid || !isPublished(props.audience.audienceStatus));
	}

	function getEmployeeCount() {
		const { audience, audienceSourceType } = props;
		return audienceSourceType === AudienceSourceType.Hris ||
			audienceSourceType === AudienceSourceType.Hierarchy ||
			audience.distributionType === DistributionType.SingleLinkWithoutDistribution
			? '--'
			: audience.employeesCount;
	}
	function getTooltip() {
		const { audience, audienceSourceType } = props;
		let tooltipText = 'Employee count is updated after survey has been published';
		let displayTooltip = false;

		if (
			(audienceSourceType === AudienceSourceType.Hris || audienceSourceType === AudienceSourceType.FileUpload) &&
			audience.employeesCount === 0 &&
			audience.distributionType !== DistributionType.SingleLinkWithoutDistribution
		) {
			displayTooltip = true;

			if (audienceSourceType === AudienceSourceType.FileUpload) {
				tooltipText = 'Employee count will be updated when file finishes processing';
			}
		}

		return (
			displayTooltip && (
				<Tooltip id='employee-count-tooltip' theme={{ theme: 'compact' }}>
					{tooltipText}
				</Tooltip>
			)
		);
	}
};
